import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Search,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  Leaf,
  X,
  FileText,
  Users,
  Target,
  Package,
  Building
} from 'lucide-react';
import Button from '../Button/Button';
import Alert from '../Alert/Alert';
import { formatRoleName, USER_ROLES } from '../../../utils/constants';
import { allNavigationItems } from '../../../config/navigationItems';
import { hasPermission, hasHRMSPermission, hasCRMPermission, isEmployee } from '../../../utils/navigationUtils';
import NotificationDropdown from './NotificationDropdown';


const Header = ({
  onToggleSidebar,
  onToggleMobileMenu,
  isSidebarCollapsed,
  user,
  onLogout,
  onNavigate,
  notifications = []
}) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  // Refs for dropdown containers
  const notificationsRef = useRef(null);
  const userMenuRef = useRef(null);

  // Get user from localStorage as fallback if context user doesn't have permissions
  const getUserWithPermissions = () => {
    if (user && user.granularPermissions) {
      return user;
    }

    // Try to get from localStorage
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser && parsedUser.granularPermissions) {
          return parsedUser;
        }
      }
    } catch (e) {
    }

    return user;
  };

  const userWithPermissions = getUserWithPermissions();

  // Generate searchable modules based on user permissions
  const searchableModules = useMemo(() => {
    if (!userWithPermissions) return [];
    const userRole = userWithPermissions.role || USER_ROLES.EMPLOYEE;
    const items = [];

    // Helper to flatten navigation items
    const processItems = (itemObj, parentLabel = '', inheritedAccess = false) => {
      Object.values(itemObj).forEach(item => {
        // PERMISSION CHECK: Verify access rights matching DynamicSidebar logic
        let hasAccess = false;

        if (item.id === 'dashboard' || item.id === 'notifications') {
          hasAccess = true; // Everyone has access to Dashboard and Notifications
        } else if (item.id === 'employee_self_service') {
          hasAccess = userRole !== USER_ROLES.SUPER_ADMIN; // Hide for Super Admin
        } else if (item.checkHRMSPermission) {
          hasAccess = hasHRMSPermission(userWithPermissions, userRole);
        } else if (item.checkCRMPermission) {
          hasAccess = hasCRMPermission(userWithPermissions, userRole);
        } else if (item.permissionKey) {
          hasAccess = hasPermission(userWithPermissions, userRole, item.permissionKey);
        } else if (inheritedAccess) {
          // Inherit access for nested sub-items (Level 3) if parent (Level 2) passed its check,
          // but respect role restrictions if specified
          if (Array.isArray(item.roles) && item.roles.length > 0) {
            hasAccess = item.roles.includes(userRole) || userRole === USER_ROLES.SUPER_ADMIN;
          } else {
            hasAccess = true;
          }
        } else if (Array.isArray(item.roles) && item.roles.length > 0) {
          // Fallback to role-based access if no specific permissionKey is defined
          hasAccess = item.roles.includes(userRole) || userRole === USER_ROLES.SUPER_ADMIN;
        }

        if (hasAccess) {
          if (item.path) {
            const displayTitle = parentLabel ? `${parentLabel} > ${item.label}` : item.label;
            items.push({
              id: item.id,
              title: displayTitle,
              label: item.label,
              description: `Navigate to ${item.label}`,
              type: 'module',
              icon: item.icon,
              path: item.path
            });
          }

          if (item.subItems) {
            // Level 3 items inherit access if Level 2 has an explicit permissionKey that passed
            const shouldInherit = !!item.permissionKey;
            processItems(item.subItems, item.label, shouldInherit);
          }
        }
      });
    };

    processItems(allNavigationItems);

    // Remove duplicates based on path and title
    return items.filter((item, index, self) =>
      index === self.findIndex((t) => t.path === item.path && t.title === item.title)
    );
  }, [userWithPermissions]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (term.length > 0) {
      const filtered = searchableModules.filter(item =>
        item.title.toLowerCase().includes(term.toLowerCase()) ||
        item.label.toLowerCase().includes(term.toLowerCase())
      );
      setSearchResults(filtered);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleSearchResultClick = (result) => {
    if (onNavigate) {
      onNavigate(result.id);
    } else {
      navigate(result.path);
    }
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close notifications and user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close notifications dropdown if clicked outside
      if (showNotifications && notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }

      // Close user menu dropdown if clicked outside
      if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, showUserMenu]);

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleNotificationsToggle = () => {
    setShowNotifications(!showNotifications);
  };

  const unreadNotifications = notifications.filter(notification => !notification.read).length;

  const handleLogoutClick = () => {
    setShowLogoutAlert(true);
    setShowUserMenu(false);
  };

  const handleLogoutConfirm = () => {
    onLogout();
  };

  const profileImgSrc = user?.profilePicture
    ? (user.profilePicture.startsWith('http')
      ? user.profilePicture
      : `${process.env.REACT_APP_API_BASE_URL}/uploads/${user.profilePicture}`)
    : null;

  return (
    <header className="bg-gradient-to-r from-agrosaf-green to-agrosaf-blue border-b border-white/20 px-3 sm:px-6 py-3 flex items-center justify-between shadow-lg h-16">
      {/* Left Section */}
      <div className="flex items-center space-x-4">
        {/* Sidebar Toggle - Hide Menu Button (for all screen sizes) */}
        <Button
          variant="ghost"
          size="sm"
          icon={<Menu className="h-5 w-5 text-white" />}
          onClick={onToggleSidebar}
          className="hover:bg-white/20"
        />

        <div className="hidden md:flex items-center space-x-4">
          <div className="relative search-container">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search modules..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="px-6 py-2 bg-white/90 backdrop-blur-sm border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 w-64 lg:w-[500px] text-gray-900 placeholder-gray-500 transition-all duration-300"
            />
            {searchTerm && (
              <button
                onClick={handleSearchClear}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            )}

            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-80 overflow-y-auto">
                <div className="p-2">
                  {searchResults.map((result) => {
                    const IconComponent = result.icon || Search;
                    return (
                      <div
                        key={result.id}
                        onClick={() => handleSearchResultClick(result)}
                        className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className="flex-shrink-0">
                          <IconComponent className="h-5 w-5 text-gray-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {result.title}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {result.description}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${result.type === 'module' ? 'bg-blue-100 text-blue-800' :
                            result.type === 'employee' ? 'bg-green-100 text-green-800' :
                              result.type === 'opportunity' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                            {result.type}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No Results Message */}
            {showSearchResults && searchResults.length === 0 && searchTerm && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                <div className="p-4 text-center text-gray-500">
                  <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No results found for "{searchTerm}"</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center space-x-4">
        {/* Notifications */}
        <NotificationDropdown />


        {/* User Menu */}
        <div className="relative" ref={userMenuRef}>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUserMenuToggle}
            className="flex items-center space-x-2 hover:bg-white/20"
          >
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 overflow-hidden">
              {profileImgSrc ? (
                <img src={profileImgSrc} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="h-4 w-4 text-white" />
              )}
            </div>
            <div className="hidden md:block text-left min-w-0 max-w-[150px]">
              <p className="text-sm font-medium text-white truncate">
                {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.firstName || user?.email || 'User'}
              </p>
              <p className="text-xs text-white/80 truncate">
                {formatRoleName(user?.role)}
              </p>
            </div>
            <ChevronDown className="h-4 w-4 text-white/80" />
          </Button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-44 sm:w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-agrosaf-green to-agrosaf-blue rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {profileImgSrc ? (
                      <img src={profileImgSrc} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <User className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 break-words">
                      {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.firstName || user?.email || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 break-words">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>
              <div className="py-2">
                <>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate('/profile');
                      setShowUserMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2 transition-colors cursor-pointer"
                  >
                    <User className="h-4 w-4" />
                    <span>Profile</span>
                  </button>
                  <hr className="my-2" />
                </>
                <button
                  onClick={handleLogoutClick}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirmation Alert */}
      <Alert
        isOpen={showLogoutAlert}
        onClose={() => setShowLogoutAlert(false)}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
        type="warning"
        showCancel={true}
        confirmText="YES"
        cancelText="NO"
        onConfirm={handleLogoutConfirm}
        onCancel={() => setShowLogoutAlert(false)}
      />
    </header>
  );
};

export default Header;
